"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.prettyTransaction = exports.buildSendAndCheck = exports.sendTransaction = exports.sendAndConfirmTransaction = exports.buildAndSignTransactionsFromIxWithSigners = exports.signTransactionReturns = exports.buildTransactionsFromIxWithSigners = exports.formatExplorerLink = exports.formatExplorerMessageLink = exports.buildAndSignTransactions = exports.buildAndSignTransaction = void 0;
const web3_js_1 = require("@solana/web3.js");
const assert_1 = __importDefault(require("assert"));
const neverthrow_1 = require("neverthrow");
const util_1 = require("../util");
function buildAndSignTransaction(instructions, feePayer, connectionOrRbh) {
    return __awaiter(this, void 0, void 0, function* () {
        const out = (yield buildAndSignTransactions([{ ixs: instructions, connectionOrRbh }], feePayer))[0];
        if (out === undefined) {
            throw new Error('Transaction was not built correctly');
        }
        return out;
    });
}
exports.buildAndSignTransaction = buildAndSignTransaction;
function buildAndSignTransactions(transactions, feePayer) {
    return __awaiter(this, void 0, void 0, function* () {
        return yield buildAndSignTransactionsFromIxWithSigners(yield Promise.all(transactions.map(({ ixs, connectionOrRbh }) => __awaiter(this, void 0, void 0, function* () {
            return {
                ixs: (0, util_1.normalizeArray)(yield Promise.all((0, util_1.normalizeArray)(ixs).map((ix) => ix(feePayer)))).flat(),
                connectionOrRbh,
            };
        }))), feePayer);
    });
}
exports.buildAndSignTransactions = buildAndSignTransactions;
function formatExplorerMessageLink(transaction, connection) {
    const clusterUrl = encodeURIComponent(connection.rpcEndpoint);
    const serializedTransaction = transaction.serializeMessage();
    const message = encodeURIComponent(serializedTransaction.toString('base64'));
    return `https://explorer.solana.com/tx/inspector?message=${message}&cluster=custom&customUrl=${clusterUrl}`;
}
exports.formatExplorerMessageLink = formatExplorerMessageLink;
function formatExplorerLink(signature, connection) {
    const clusterUrl = encodeURIComponent(connection.rpcEndpoint);
    return `https://explorer.solana.com/tx/${signature}?cluster=custom&customUrl=${clusterUrl}`;
}
exports.formatExplorerLink = formatExplorerLink;
function buildTransactionsFromIxWithSigners(transactions, feePayer) {
    return __awaiter(this, void 0, void 0, function* () {
        const signers = new Map();
        if ('signer' in feePayer) {
            const feePayerKey = feePayer.signer.publicKey().toBase58();
            const feePayerEntry = {
                signer: feePayer.signer,
                transactions: new Set(),
            };
            for (let x = 0; x < transactions.length; x++) {
                feePayerEntry.transactions.add(x);
            }
            signers.set(feePayerKey, feePayerEntry);
        }
        const feePayerKey = 'signer' in feePayer ? feePayer.signer.publicKey() : feePayer.key;
        return {
            tx: yield Promise.all(transactions.map(({ ixs: ixArray, connectionOrRbh }, index) => __awaiter(this, void 0, void 0, function* () {
                const instructions = (0, util_1.normalizeArray)(ixArray);
                const tx = new web3_js_1.Transaction();
                const ixs = instructions.map((ix) => ix.instruction);
                tx.add(...ixs);
                for (const signer of instructions.flatMap((ix) => ix.signers)) {
                    const key = signer.publicKey().toBase58();
                    const signerEntry = signers.get(key);
                    if (signerEntry === undefined) {
                        signers.set(key, { signer, transactions: new Set([index]) });
                    }
                    else {
                        signerEntry.transactions.add(index);
                    }
                }
                let rbh;
                let commitment;
                if ('connection' in connectionOrRbh) {
                    rbh = yield connectionOrRbh.connection.getLatestBlockhash(connectionOrRbh.commitment);
                    commitment =
                        connectionOrRbh.commitment ||
                            connectionOrRbh.connection.commitment ||
                            'processed';
                }
                else {
                    rbh = connectionOrRbh.rbh;
                    commitment = connectionOrRbh.commitment;
                }
                tx.recentBlockhash = rbh.blockhash;
                tx.feePayer = feePayerKey;
                tx.lastValidBlockHeight = rbh.lastValidBlockHeight;
                const missingSigners = tx.signatures
                    .filter((tx) => tx.signature === null)
                    .map((sig) => ({
                    key: sig.publicKey,
                    indexes: tx.instructions
                        .map((ix, index) => ({
                        instructionProgram: ix.programId,
                        instructionIndex: index,
                        keyIndexes: ix.keys
                            .map((k, index) => k.isSigner && k.pubkey.equals(sig.publicKey) ? index : null)
                            .filter((i) => i !== null),
                    }))
                        .filter((result) => result.keyIndexes.length > 0),
                }));
                if (missingSigners.length > 0) {
                    throw new Error('Transaction not fully signed: ' +
                        JSON.stringify(missingSigners, null, 2));
                }
                return {
                    transaction: tx,
                    rbh,
                    commitment,
                };
            }))),
            signers,
        };
    });
}
exports.buildTransactionsFromIxWithSigners = buildTransactionsFromIxWithSigners;
function signTransactionReturns(unsignedTransactions, signers) {
    return __awaiter(this, void 0, void 0, function* () {
        const signTxns = ({ signer, transactions }) => __awaiter(this, void 0, void 0, function* () {
            const txsToSign = unsignedTransactions
                .map((tx, index) => ({ tx, index }))
                .filter(({ index }) => transactions.has(index));
            const newTxs = yield signer.signAll(txsToSign.map(({ tx }) => tx.transaction));
            (0, assert_1.default)(newTxs.length === txsToSign.length);
            for (let x = 0; x < txsToSign.length; x++) {
                unsignedTransactions[txsToSign[x].index].transaction = newTxs[x];
            }
        });
        for (const signer of signers.values()) {
            if (signer.signer.requiresAsync()) {
                yield signTxns(signer);
            }
        }
        for (const signer of signers.values()) {
            if (!signer.signer.requiresAsync()) {
                yield signTxns(signer);
            }
        }
        return unsignedTransactions;
    });
}
exports.signTransactionReturns = signTransactionReturns;
function buildAndSignTransactionsFromIxWithSigners(transactions, feePayer) {
    return __awaiter(this, void 0, void 0, function* () {
        const { tx: unsignedTransactions, signers } = yield buildTransactionsFromIxWithSigners(transactions, {
            signer: feePayer,
        });
        return signTransactionReturns(unsignedTransactions, signers);
    });
}
exports.buildAndSignTransactionsFromIxWithSigners = buildAndSignTransactionsFromIxWithSigners;
/**
 * @deprecated Use {@link sendTransaction} instead.
 */
function sendAndConfirmTransaction(tx, connection, sendOptions, serializeConfig) {
    return __awaiter(this, void 0, void 0, function* () {
        const signature = yield connection.sendRawTransaction(tx.transaction.serialize(serializeConfig), sendOptions);
        return {
            signature,
            signatureResult: (yield connection.confirmTransaction(Object.assign(Object.assign({}, tx.rbh), { signature: signature }), tx.commitment)).value,
        };
    });
}
exports.sendAndConfirmTransaction = sendAndConfirmTransaction;
/**
 * Sends and confirms a transaction
 * @param transaction The transaction to send
 * @param connection The connection to send the transaction to
 * @param options Options to send transaction
 */
function sendTransaction(transaction, connection, options) {
    return __awaiter(this, void 0, void 0, function* () {
        const rawTransaction = transaction.transaction.serialize(options === null || options === void 0 ? void 0 : options.serializeConfig);
        const commitment = (options === null || options === void 0 ? void 0 : options.commitment) || 'confirmed';
        const signature = yield connection.sendRawTransaction(rawTransaction, options === null || options === void 0 ? void 0 : options.sendOptions);
        const result = yield connection.confirmTransaction(Object.assign({ signature }, transaction.rbh), commitment);
        if (result.value.err !== null) {
            return { context: result.context, value: (0, neverthrow_1.err)(result.value.err) };
        }
        else {
            return { context: result.context, value: (0, neverthrow_1.ok)(signature) };
        }
    });
}
exports.sendTransaction = sendTransaction;
/**
 * @deprecated Use {@link buildAndSignTransaction} and {@link sendTransaction} instead.
 * @param instructions
 * @param feePayer
 * @param connection
 * @param options
 */
function buildSendAndCheck(instructions, feePayer, connection, options) {
    var _a, _b, _c;
    return __awaiter(this, void 0, void 0, function* () {
        instructions = (0, util_1.normalizeArray)(instructions);
        const commitment = (options === null || options === void 0 ? void 0 : options.commitment) || 'confirmed';
        let tx = yield buildAndSignTransaction(instructions, feePayer, {
            connection,
            commitment,
        });
        if (options === null || options === void 0 ? void 0 : options.postTransactionHandler) {
            tx = (_a = options.postTransactionHandler(tx)) !== null && _a !== void 0 ? _a : tx;
        }
        const { signature, signatureResult } = yield sendAndConfirmTransaction(tx, connection, Object.assign({ skipPreflight: false }, options === null || options === void 0 ? void 0 : options.sendOptions), options === null || options === void 0 ? void 0 : options.serializeConfig);
        if (signatureResult.err !== null) {
            if (!(options === null || options === void 0 ? void 0 : options.suppressLogging)) {
                console.error('Transaction signature: ', signature);
                const transaction = yield connection.getTransaction(signature, {
                    commitment,
                    maxSupportedTransactionVersion: 1,
                });
                console.error('Transaction logs: ', (_b = transaction === null || transaction === void 0 ? void 0 : transaction.meta) === null || _b === void 0 ? void 0 : _b.logMessages);
                console.error(`Explorer link: ${formatExplorerLink(signature, connection)}`);
            }
            throw new Error('Transaction error: ' + JSON.stringify(signatureResult.err, null, 2));
        }
        else if (options != undefined &&
            options.suppressLogging != undefined &&
            !options.suppressLogging) {
            // Please don't delete this xD. Is for the case we want to print the logs even if the IX success
            console.info('Transaction signature: ', signature);
            const transaction = yield connection.getTransaction(signature, {
                commitment,
                maxSupportedTransactionVersion: 1,
            });
            // transaction?.transaction.message.getAccountKeys();
            console.info('Transaction logs: ', (_c = transaction === null || transaction === void 0 ? void 0 : transaction.meta) === null || _c === void 0 ? void 0 : _c.logMessages);
        }
        return signature;
    });
}
exports.buildSendAndCheck = buildSendAndCheck;
function prettyTransaction(tx) {
    var _a;
    return {
        signatures: tx.signatures.map((sig) => ({
            publicKey: sig.publicKey.toBase58(),
            signature: sig.signature !== null,
        })),
        instructions: tx.instructions.map((ix) => ({
            keys: ix.keys.map(prettyAccountMeta),
            programId: ix.programId.toBase58(),
        })),
        feePayer: (_a = tx.feePayer) === null || _a === void 0 ? void 0 : _a.toBase58(),
        recentBlockhash: tx.recentBlockhash,
        lastValidBlockHeight: tx.lastValidBlockHeight,
    };
}
exports.prettyTransaction = prettyTransaction;
function prettyAccountMeta(meta) {
    return Object.assign(Object.assign({}, meta), { pubkey: meta.pubkey.toBase58() });
}
//# sourceMappingURL=transactionHandling.js.map